import { X } from "lucide-react";

import React, { useMemo, useState } from "react";
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Modal,
  TextField,
} from "@material-ui/core";
import clsx from "clsx";

import { Button } from "@/design-system/components/Button/Button";

import {
  useModalStyles,
  RoleSelector,
  RoleInviteDefault,
  RoleAdmin,
  UserEnvironmentSelectorInviteModal,
} from "../TeamPage.jsx";
import isEmailValid from "./isEmailValid";

type InviteUserModalProps = {
  open: boolean;
  onClose: () => void;
  onEmailChange: (email: string) => void;
  inviteUserRole: string;
  onRoleChange: (role: string) => void;
  onInvite: () => void;
  inviteSelectedEnvs: { [key: string]: boolean };
  setInviteSelectedEnvs: (envs: { [key: string]: boolean }) => void;
  setHasEmailError: (hasError: boolean) => void;
};

function InviteUserModal({
  open,
  onClose,
  onEmailChange,
  inviteUserRole,
  onRoleChange,
  onInvite,
  inviteSelectedEnvs,
  setInviteSelectedEnvs,
  setHasEmailError,
}: InviteUserModalProps) {
  const classes = useModalStyles();

  const [email, setEmail] = useState<string>("");

  const emailIsValid = useMemo(() => {
    return !isEmailValid(email);
  }, [email]);

  const emailErrorMessage = useMemo(() => {
    if (email.trim() === "") {
      return "Email is required.";
    }

    if (emailIsValid) {
      return "Email provided is not valid.";
    }

    return "";
  }, [email, emailIsValid]);

  const handleEmailChange = (email: string) => {
    setEmail(email);

    if (!isEmailValid(email)) {
      setHasEmailError(true);
      return;
    }

    setHasEmailError(false);
    onEmailChange(email);
  };

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title="Invite Teammate"
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<X onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <p className={clsx(classes.bold)} id="email">
            Email
          </p>
          <TextField
            fullWidth
            error={emailErrorMessage !== ""}
            helperText={emailErrorMessage}
            id="invite-user-email"
            label="Email"
            type="email"
            name="invite-user-email"
            value={email}
            variant="outlined"
            className={clsx(classes.inputField)}
            onChange={(e) => handleEmailChange(e.target.value.trim())}
          />
          <br /> <br />
          <>
            <RoleSelector
              defaultRole={RoleInviteDefault}
              onRoleChange={(newRole) => onRoleChange(newRole)}
            />
            {inviteUserRole && inviteUserRole !== RoleAdmin && (
              <UserEnvironmentSelectorInviteModal
                inviteSelectedEnvs={inviteSelectedEnvs}
                setInviteSelectedEnvs={setInviteSelectedEnvs}
              />
            )}
          </>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button disabled={emailErrorMessage !== ""} onClick={onInvite}>
            Send Invite
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

export default InviteUserModal;
